import { memo, useState, useEffect, type CSSProperties } from "react";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { Trans, useTranslation } from "react-i18next";
import {
  AuraLayout,
  BackArrow,
  BodyCopy,
  Button,
  ElevatedCircle,
  GradientText,
  Layout,
} from "lola-framework-ui-test";
import {
  APP_ROUTES,
  flavorClassName,
  isFlavorB,
} from "../constants";
import KapitalRLogo from "../assets/KapitalLogoR.svg";
import "../styles/landingpage.style.css";
import "../styles/legal.style.css";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { acceptUserTerms } from "../services/api";
import { safeCapture } from "../utils/posthog";

export const LandingPage = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    iconColors,
    dropzoneColors,
    buttonShowIcon,
    buttonSize,
  } = colorConfig;
  const { redirectTo } = useRouteWithToken();
  const { session, setSession } = useSessionStore();
  const [isESignChecked, setIsESignChecked] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(false);

  console.log(dropzoneColors)

  useEffect(() => {
    safeCapture("ui.onboarding.terms.screen.view");
  }, []);

  const handleContinue = async () => {
    try {
      await acceptUserTerms();
      if (session.data) {
        setSession({
          ...session,
          data: { ...session.data, termsAccepted: true },
        });
      }
      safeCapture("ux.onboarding.terms.screen.submit", { success: true });
    } catch (err) {
      console.error("Failed to persist terms acceptance:", err);
      safeCapture("ux.onboarding.terms.screen.submit", {
        success: false,
        error: "Failed to persist terms acceptance",
        error_code: "TERMS_ACCEPT_FAILED",
      });
    }
    redirectTo(APP_ROUTES.TRANSACTIONS_PAGE);
  };

  const content = isFlavorB ? (
    <>
      <img
        src={KapitalRLogo}
        alt="app logo"
        className="landing-logo gradient-card glass"
      />
      <h1 className="foreground highlight text-centered">
        <Trans i18nKey="landing.startTransferring" />
        <span className="span-block lola-title-h1 h1">
          {t("landing.followSteps2")}
        </span>
      </h1>
    </>
  ) : (
    <>
      <ElevatedCircle
        shadowVariant="inset"
      >
        <BackArrow size={60} className="rotate-90" colors={dropzoneColors} />
      </ElevatedCircle>
      <section className="landing-container">
        <h1 className="foreground highlight text-centered">
          {t("landing.greeting")}
          <span className="span-block lola-title-h1 h1">
            {t("landing.toTransfer")}
          </span>
        </h1>
        <p className="foreground text-centered subtitle">
          {t("landing.followSteps")}
          <span className="subtitle-span">
            <Trans i18nKey="landing.madeSimple" />
          </span>
        </p>
      </section>
    </>
  );
  const contentAlignClassName = isFlavorB ? "content-center" : "";
  return (
    <AuraLayout
      colorConfig={colorConfig}
      className={`${flavorClassName}`}
    >
      <Layout.Content
        className={`landing-section content-center ${contentAlignClassName}`}
      >
        {content}
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <section className="landing-policies">
            <input
              id="e-sign"
              type="checkbox"
              checked={isESignChecked}
              onChange={(e) => setIsESignChecked(e.target.checked)}
              style={{ "--backgroundColor": iconColors[0] } as CSSProperties}
            />
            <BodyCopy className="footer-text terms-text footer-link muted-foreground">
              <label htmlFor="e-sign">
                {t("landing.footer.esign-content")}
              </label>
              <GradientText
                as="label"
                className="link"
                textColor="#0057FF"
                onClick={() => {
                  safeCapture("ui.onboarding.terms.legal_link.click", {
                    doc: "esign",
                  });
                  redirectTo(`${APP_ROUTES.LEGAL_PAGE}?doc=esign`);
                }}
              >
                {t("landing.footer.esign-link")}
              </GradientText>
            </BodyCopy>
          </section>
          <section className="landing-policies">
            <input
              id="check-confirm"
              type="checkbox"
              checked={isTermsChecked}
              onChange={(e) => setIsTermsChecked(e.target.checked)}
              style={{ "--backgroundColor": iconColors[0] } as CSSProperties}
            />
            <BodyCopy className="footer-text terms-text footer-link muted-foreground">
              <label>{t("landing.footer.terms-content")}</label>

              <GradientText
                as="label"
                className="link"
                textColor="#0057FF"
                onClick={() => {
                  safeCapture("ui.onboarding.terms.legal_link.click", {
                    doc: "terms",
                  });
                  redirectTo(`${APP_ROUTES.LEGAL_PAGE}?exclude=esign`);
                }}
              >
                {t("landing.footer.terms-link")}
              </GradientText>
              <label>{t("landing.footer.terms-content-2")}</label>
            </BodyCopy>
          </section>
          <Button
            disabled={!isTermsChecked || !isESignChecked}
            background={backgroundBtn}
            color={textColorBtn}
            showIcon={buttonShowIcon}
            size={buttonSize ?? "small"}
            onClick={handleContinue}
          >
            {t("common.continue")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
